import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { EvaluationAnswerValue, PendingQuestion } from '../types/evaluation'

import { EVALUATIONS_QUERY_KEY } from './useEvaluations'

import { api } from '@/shared/services/api'

interface SaveAnswerInput {
  questionId: number
  value: EvaluationAnswerValue
}

/**
 * Grava a resposta de UMA questão. Não finaliza nada: é o que dá retomada ao
 * aluno sem creditar pontos pela metade.
 */
export function useSaveAnswer(evaluationId: number | string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ questionId, value }: SaveAnswerInput) => {
      const { data } = await api.post(`/user/evaluations/${evaluationId}/answers`, {
        record: { evaluation_question_id: questionId, value }
      })
      return data.record
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/user/evaluations', String(evaluationId)]
      })
    }
  })
}

export interface FinishError {
  pendingQuestions: PendingQuestion[]
  message: string
}

export function useFinishEvaluation(evaluationId: number | string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/user/evaluations/${evaluationId}/finish`)
      return data.record
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['/user/evaluations', String(evaluationId)]
      })
      queryClient.invalidateQueries({ queryKey: EVALUATIONS_QUERY_KEY })
    }
  })
}

/**
 * O 422 da finalização carrega QUAIS questões faltam — mostrar só "preencha
 * tudo" numa avaliação de 40 questões é o mesmo que não dizer nada.
 */
export function parseFinishError(error: unknown): FinishError {
  const response = (
    error as {
      response?: {
        data?: {
          errors?: { evaluation_questions?: PendingQuestion[]; base?: string[] }
          error_message?: string
        }
      }
    }
  )?.response?.data

  return {
    pendingQuestions: response?.errors?.evaluation_questions ?? [],
    message:
      response?.errors?.base?.[0] ??
      response?.error_message ??
      'Não foi possível enviar a avaliação.'
  }
}
